import { createApi } from "@reduxjs/toolkit/query/react";
import { useMemo } from "react";
import { extendedBaseQuery } from "../basequery";

export const sponsorshipApi = createApi({
  reducerPath: "sponsorshipApi",
  baseQuery: extendedBaseQuery,
  tagTypes: ["SponsorshipAnalytics", "SponsoredUsers"],
  endpoints: (builder) => ({
    getOrganizationAnalytics: builder.query({
      query: (params) => {
        const query = {};
        if (params?.organizationId) query.organizationId = params.organizationId;
        if (params?.duration) query.duration = params.duration;
        if (params?.eventPlace) query.eventPlace = params.eventPlace;
        if (params?.tag) query.tag = params.tag;
        if (params?.startDate) query.startDate = params.startDate;
        if (params?.endDate) query.endDate = params.endDate;
        if (params?.userType) query.userType = params.userType;
        if (params?.usageCohort) query.usageCohort = params.usageCohort;

        return {
          url: `smarton-analytics-report?${new URLSearchParams(query).toString()}`,
          method: "GET",
        };
      },
      providesTags: ["SponsorshipAnalytics"],
    }),
    getSponsoredUsers: builder.query({
      async queryFn(params, _queryApi, _extraOptions, fetchWithBQ) {
        const queryParams = new URLSearchParams();
        if (params?.organizationId) queryParams.append("organizationId", params.organizationId);
        if (params?.search) queryParams.append("search", params.search);
        if (params?.limit) queryParams.append("limit", params.limit);
        if (params?.skip) queryParams.append("skip", params.skip);
        if (params?.filter) queryParams.append("filter", params.filter);
        if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
        if (params?.orderBy) queryParams.append("orderBy", params.orderBy);
        if (params?.status) {
          // Map frontend display labels to the backend DB enum values
          const STATUS_TO_DB = {
            "in process": "pending_registration",
            pending: "pending",
            approved: "approved",
            rejected: "rejected",
          };
          const dbStatus = STATUS_TO_DB[params.status] ?? params.status;
          queryParams.append("status", dbStatus);
        }
        if (params?.addedByType) queryParams.append("addedByType", params.addedByType);

        const result = await fetchWithBQ({
          url: `organization-sponsored-users?${queryParams.toString()}`,
          method: "GET",
        });

        return result.error ? { error: result.error } : { data: result.data };
      },
      providesTags: ["SponsoredUsers"],
    }),
    createSponsoredUser: builder.mutation({
      query: (body) => ({
        url: "organization-sponsored-users",
        method: "POST",
        body,
      }),
      invalidatesTags: ["SponsoredUsers"],
    }),
    approveSponsoredUser: builder.mutation({
      query: (params) => {
        const userId = typeof params === "string" ? params : params?.userId;

        return {
          url: `organization-sponsored-users/${userId}/approve`,
          method: "PATCH",
        };
      },
      invalidatesTags: ["SponsoredUsers"],
      async onQueryStarted(params, { dispatch, getState, queryFulfilled }) {
        const userId = typeof params === "string" ? params : params?.userId;
        try {
          const { data: response } = await queryFulfilled;
          if (response?.success && response?.data) {
            const updatedUser = response.data;
            const { queries } = getState().sponsorshipApi;
            Object.values(queries).forEach((queryVal) => {
              if (queryVal?.endpointName === "getSponsoredUsers") {
                const { originalArgs } = queryVal;
                dispatch(
                  sponsorshipApi.util.updateQueryData(
                    "getSponsoredUsers",
                    originalArgs,
                    (draft) => {
                      if (draft?.data) {
                        if (draft.data.existingUsers) {
                          const index = draft.data.existingUsers.findIndex((u) => u.userId === userId);
                          if (index !== -1) {
                            draft.data.existingUsers[index] = {
                              ...draft.data.existingUsers[index],
                              ...updatedUser,
                            };
                          }
                        }
                        if (draft.data.nonExistingUsers) {
                          const index = draft.data.nonExistingUsers.findIndex((u) => u.userId === userId);
                          if (index !== -1) {
                            draft.data.nonExistingUsers[index] = {
                              ...draft.data.nonExistingUsers[index],
                              ...updatedUser,
                            };
                          }
                        }
                      }
                    }
                  )
                );
              }
            });
          }
        } catch (err) {
          console.error("Cache update failed:", err);
        }
      },
    }),
    rejectSponsoredUser: builder.mutation({
      query: (params) => {
        const id = typeof params === "string" ? params : params?.id;
        const reason = typeof params === "string" ? undefined : params?.reason;
        return {
          url: `organization-sponsored-users/${id}/reject`,
          method: "PATCH",
          body: reason ? { reason } : undefined,
        };
      },
      invalidatesTags: ["SponsoredUsers"],
    }),
    updateSponsoredUser: builder.mutation({
      query: ({ id, body }) => ({
        url: `organization-sponsored-users/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["SponsoredUsers"],
    }),
    deleteSponsoredUser: builder.mutation({
      query: (id) => ({
        url: `organization-sponsored-users/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SponsoredUsers"],
    }),
    deleteSponsoredUsersBulk: builder.mutation({
      query: (ids) => ({
        url: "organization-sponsored-users",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["SponsoredUsers"],
    }),
    getSponsoredUserUsage: builder.query({
      query: ({ userId, ...params }) => {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append("period", params.period);
        if (params?.date) queryParams.append("date", params.date);
        if (params?.startDate) queryParams.append("startDate", params.startDate);
        if (params?.endDate) queryParams.append("endDate", params.endDate);

        const queryString = queryParams.toString();
        return {
          url: `organization-sponsored-users/${userId}/usage${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        };
      },
      providesTags: (result, error, { userId }) => [
        { type: "SponsoredUsers", id: `${userId}-usage` },
      ],
    }),
    stopSponsoredSubscription: builder.mutation({
      query: (userId) => ({
        url: `organization-sponsored-users/${userId}/stop-subscription`,
        method: "PATCH",
      }),
      invalidatesTags: ["SponsoredUsers"],
    }),
    stopSponsoredSubscriptionsBulk: builder.mutation({
      query: (userIds) => ({
        url: "organization-sponsored-users/stop-subscriptions",
        method: "PATCH",
        body: { userIds },
      }),
      invalidatesTags: ["SponsoredUsers"],
    }),
  }),
});

export const {
  useGetOrganizationAnalyticsQuery,
  useGetSponsoredUsersQuery,
  useCreateSponsoredUserMutation,
  useApproveSponsoredUserMutation,
  useRejectSponsoredUserMutation,
  useUpdateSponsoredUserMutation,
  useDeleteSponsoredUserMutation,
  useDeleteSponsoredUsersBulkMutation,
  useGetSponsoredUserUsageQuery,
  useStopSponsoredSubscriptionMutation,
  useStopSponsoredSubscriptionsBulkMutation,
} = sponsorshipApi;

const applyFilterRules = (records, filterJson) => {
  if (!filterJson || typeof filterJson !== "string") return records;
  try {
    const parsed = JSON.parse(filterJson);
    const filters = parsed?.filters || [];
    const mainCondition = (parsed?.mainCondition || "and").toLowerCase();

    if (!filters.length) return records;

    return records.filter((row) => {
      const matchResults = filters.map((f) => {
        const field = f.field;
        const op = f.operator || f.condition;
        const value = f.value;

        if (!field || !op) return true;

        const rowVal = row[field];
        const strRowVal = rowVal !== null && rowVal !== undefined ? String(rowVal).toLowerCase().trim() : "";
        const strFilterVal = value !== null && value !== undefined && typeof value === "string" 
          ? value.toLowerCase().trim() 
          : value !== null && value !== undefined ? String(value).toLowerCase().trim() : "";

        switch (op) {
          case "startsWith":
            return strRowVal.startsWith(strFilterVal);
          case "endsWith":
            return strRowVal.endsWith(strFilterVal);
          case "contains":
            return strRowVal.includes(strFilterVal);
          case "notContains":
            return !strRowVal.includes(strFilterVal);
          case "equals":
          case "is":
            return strRowVal === strFilterVal;
          case "notEquals":
          case "not":
            return strRowVal !== strFilterVal;
          case "isEmpty":
            return strRowVal === "" || rowVal === null || rowVal === undefined;
          case "isNotEmpty":
            return strRowVal !== "" && rowVal !== null && rowVal !== undefined;
          case "isAnyOf":
            if (Array.isArray(value)) {
              return value.some((v) => String(v).toLowerCase().trim() === strRowVal);
            }
            return strRowVal.includes(strFilterVal);
          case "true":
            return rowVal === true || strRowVal === "true" || strRowVal === "yes";
          case "false":
            return rowVal === false || strRowVal === "false" || strRowVal === "no";
          case "greaterThan":
            return Number(rowVal) > Number(value);
          case "lessThan":
            return Number(rowVal) < Number(value);
          case "between":
            if (Array.isArray(value) && value.length >= 2) {
              return Number(rowVal) >= Number(value[0]) && Number(rowVal) <= Number(value[1]);
            }
            return true;
          case "exact": {
            if (!rowVal || !value) return false;
            const rDate = new Date(rowVal).toISOString().split("T")[0];
            const fDate = new Date(value).toISOString().split("T")[0];
            return rDate === fDate;
          }
          case "notExact": {
            if (!rowVal || !value) return true;
            const rDate = new Date(rowVal).toISOString().split("T")[0];
            const fDate = new Date(value).toISOString().split("T")[0];
            return rDate !== fDate;
          }
          case "after": {
            if (!rowVal || !value) return false;
            return new Date(rowVal).getTime() > new Date(value).getTime();
          }
          case "onOrAfter": {
            if (!rowVal || !value) return false;
            return new Date(rowVal).getTime() >= new Date(value).getTime();
          }
          case "before": {
            if (!rowVal || !value) return false;
            return new Date(rowVal).getTime() < new Date(value).getTime();
          }
          case "onOrBefore": {
            if (!rowVal || !value) return false;
            return new Date(rowVal).getTime() <= new Date(value).getTime();
          }
          case "betweenDate": {
            if (!rowVal || !Array.isArray(value) || value.length < 2) return true;
            const t = new Date(rowVal).getTime();
            return t >= new Date(value[0]).getTime() && t <= new Date(value[1]).getTime();
          }
          default:
            return true;
        }
      });

      if (mainCondition === "or") {
        return matchResults.some(Boolean);
      }
      return matchResults.every(Boolean);
    });
  } catch (e) {
    return records;
  }
};

const applySortRules = (records, sortBy, orderBy) => {
  if (!sortBy || !orderBy) return records;
  const isAsc = orderBy.toLowerCase() === "asc";
  return [...records].sort((a, b) => {
    const valA = a[sortBy];
    const valB = b[sortBy];
    if (valA === valB) return 0;
    if (valA === null || valA === undefined) return 1;
    if (valB === null || valB === undefined) return -1;
    if (typeof valA === "number" && typeof valB === "number") {
      return isAsc ? valA - valB : valB - valA;
    }
    const strA = String(valA).toLowerCase();
    const strB = String(valB).toLowerCase();
    return isAsc ? strA.localeCompare(strB) : strB.localeCompare(strA);
  });
};

export const useGetActiveSponsoredUsersQuery = (params, options) => {
  const queryResult = useGetSponsoredUsersQuery(params, options);
  
  return useMemo(() => {
    if (queryResult.data) {
      let existingUsers = queryResult.data.data?.existingUsers || [];
      existingUsers = existingUsers.map((user) => {
        let mappedStatus = user.status ? user.status.toLowerCase().replace("_", " ") : "";
        
        // Backward compatibility mapping for old backend status
        if (mappedStatus === "pending registration") {
          if (user.approvedAt) {
            mappedStatus = "in process";
          } else {
            mappedStatus = "pending";
          }
        }

        return {
          ...user,
          userId: user.userId || user.id || user.userUuid || "",
          status: mappedStatus,
        };
      });

      if (params?.status) {
        const targetStatuses = Array.isArray(params.status)
          ? params.status.map((s) => s.toLowerCase().replace("_", " "))
          : [params.status.toLowerCase().replace("_", " ")];

        existingUsers = existingUsers.filter((user) => {
          return targetStatuses.includes(user.status);
        });
      }

      if (params?.filter) {
        existingUsers = applyFilterRules(existingUsers, params.filter);
      }

      if (params?.sortBy && params?.orderBy) {
        existingUsers = applySortRules(existingUsers, params.sortBy, params.orderBy);
      }

      return {
        ...queryResult,
        data: {
          data: existingUsers,
          totalCount: existingUsers.length,
          columns: [
            { columnName: "name", headerName: "Name", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "contactNumber", headerName: "Contact Number", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "email", headerName: "Email", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "organizationName", headerName: "Target Organization", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "status", headerName: "Status", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "enum" },
            { columnName: "createdAt", headerName: "Created Date", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "date" },
          ]
        }
      };
    }
    return queryResult;
  }, [queryResult, params?.status, params?.filter, params?.sortBy, params?.orderBy]);
};

export const useGetNonExistentSponsoredUsersQuery = (params, options) => {
  const queryResult = useGetSponsoredUsersQuery(params, options);
  
  return useMemo(() => {
    if (queryResult.data) {
      const nonExistingUsers = queryResult.data.data?.nonExistingUsers || [];
      let mapped = nonExistingUsers.map((user) => {
        let mappedStatus = user.status ? user.status.toLowerCase().replace("_", " ") : "";
        
        // Backward compatibility mapping for old backend status
        if (mappedStatus === "pending registration") {
          if (user.approvedAt) {
            mappedStatus = "in process";
          } else {
            mappedStatus = "pending";
          }
        }

        return {
          ...user,
          userId: user.userId || user.id || user.userUuid || "",
          status: mappedStatus,
        };
      });

      if (params?.status) {
        const targetStatuses = Array.isArray(params.status)
          ? params.status.map((s) => s.toLowerCase().replace("_", " "))
          : [params.status.toLowerCase().replace("_", " ")];

        mapped = mapped.filter((user) => {
          return targetStatuses.includes(user.status);
        });
      }

      if (params?.filter) {
        mapped = applyFilterRules(mapped, params.filter);
      }

      if (params?.sortBy && params?.orderBy) {
        mapped = applySortRules(mapped, params.sortBy, params.orderBy);
      }

      return {
        ...queryResult,
        data: {
          data: mapped,
          totalCount: mapped.length,
          columns: [
            { columnName: "name", headerName: "Name", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "contactNumber", headerName: "Contact Number", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "email", headerName: "Email", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "organizationName", headerName: "Target Organization", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "status", headerName: "Status", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "enum" },
            { columnName: "createdAt", headerName: "Created Date", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "date" },
          ]
        }
      };
    }
    return queryResult;
  }, [queryResult, params?.status, params?.filter, params?.sortBy, params?.orderBy]);
};

export const useGetConsolidatedSponsoredUsersQuery = (params, options) => {
  const queryResult = useGetSponsoredUsersQuery(params, options);
  
  return useMemo(() => {
    if (queryResult.data) {
      const existingUsers = queryResult.data.data?.existingUsers || [];
      const nonExistingUsers = queryResult.data.data?.nonExistingUsers || [];
      const donorRequestedUsers = queryResult.data.data?.donorRequestedUsers || [];
      let merged = [...existingUsers, ...nonExistingUsers, ...donorRequestedUsers];

      // Sort newest-first by createdAt before any filtering so the order is
      // stable regardless of which bucket each record came from.
      merged.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });

      // 1. Process "Added By" display column value and normalize/map status
      merged = merged.map((user) => {
        const addedBy = user.addedBType || user.addedByType || "";
        let mappedStatus = user.status ? user.status.toLowerCase().replace("_", " ") : "";
        
        // Backward compatibility mapping for old backend status
        if (mappedStatus === "pending registration") {
          if (user.approvedAt) {
            mappedStatus = "in process";
          } else {
            mappedStatus = "pending";
          }
        }

        // Title-case the addedBy value for consistent display in tables and drawers
        const addedByLower = (addedBy || "").toLowerCase();
        const addedByDisplay =
          addedByLower === "organization" || addedByLower === "org" ? "Organization"
          : addedByLower === "admin" ? "Admin"
          : addedByLower === "support" ? "Support"
          : "--";

        return {
          ...user,
          userId: user.userId || user.id || user.userUuid || "",
          addedBTypeDisplay: addedByDisplay,
          status: mappedStatus,
        };
      });

      // 2. Client-side Approved Date Filtering
      if (params?.approvedDate) {
        merged = merged.filter((user) => {
          if (!user.approvedAt) return false;
          const approvedDateStr = user.approvedAt.split("T")[0]; // "YYYY-MM-DD"
          return approvedDateStr === params.approvedDate;
        });
      }

      // 3. Tab-specific Status and Approval checks
      if (params?.status) {
        const targetStatuses = Array.isArray(params.status)
          ? params.status.map((s) => s.toLowerCase().replace("_", " "))
          : [params.status.toLowerCase().replace("_", " ")];

        merged = merged.filter((user) => {
          return targetStatuses.includes(user.status);
        });
      }

      // 4. Client-side "Added By" Filtering
      if (params?.addedByType && params.addedByType !== "all") {
        const targetAddedBy = params.addedByType.toLowerCase();
        merged = merged.filter((user) => {
          const rawAddedBy = (user.addedBType || user.addedByType || "").toLowerCase();
          return rawAddedBy === targetAddedBy;
        });
      }

      // 5. Drawer Filter rules ("Select Filter", "Select Condition", "Value")
      if (params?.filter) {
        merged = applyFilterRules(merged, params.filter);
      }

      // 6. Sorting rules
      if (params?.sortBy && params?.orderBy) {
        merged = applySortRules(merged, params.sortBy, params.orderBy);
      }

      return {
        ...queryResult,
        data: {
          data: merged,
          totalCount: merged.length,
          statusCounts: queryResult.data.statusCounts ?? null,
          columns: [
            { columnName: "name", headerName: "Name", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "contactNumber", headerName: "Contact Number", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "email", headerName: "Email", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "status", headerName: "Status", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "enum" },
            { columnName: "addedBTypeDisplay", headerName: "Added By", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "createdAt", headerName: "Created Date", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "date" },
          ]
        }
      };
    }
    return queryResult;
  }, [queryResult, params?.status, params?.approvedDate, params?.addedByType, params?.filter, params?.sortBy, params?.orderBy]);
};

export const useGetApprovedSponsoredUsersQuery = (params, options) => {
  const queryResult = useGetSponsoredUsersQuery(params, options);
  
  return useMemo(() => {
    if (queryResult.data) {
      const existingUsers = queryResult.data.data?.existingUsers || [];
      const mapped = existingUsers.map((user) => {
        let mappedStatus = user.status ? user.status.toLowerCase().replace("_", " ") : "";
        
        // Backward compatibility mapping for old backend status
        if (mappedStatus === "pending registration") {
          if (user.approvedAt) {
            mappedStatus = "in process";
          } else {
            mappedStatus = "pending";
          }
        }

        return {
          ...user,
          userId: user.userId || user.id || user.userUuid || "",
          status: mappedStatus,
        };
      });

      let approvedUsers = mapped.filter((user) => user.status === "approved");

      if (params?.filter) {
        approvedUsers = applyFilterRules(approvedUsers, params.filter);
      }

      if (params?.sortBy && params?.orderBy) {
        approvedUsers = applySortRules(approvedUsers, params.sortBy, params.orderBy);
      }

      return {
        ...queryResult,
        data: {
          data: approvedUsers,
          totalCount: approvedUsers.length,
          columns: [
            { columnName: "name", headerName: "Name", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "email", headerName: "Email", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "contactNumber", headerName: "Contact Number", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "organizationName", headerName: "Organization", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
            { columnName: "approvedAt", headerName: "Approved Date", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "date" },
          ]
        }
      };
    }
    return queryResult;
  }, [queryResult, params?.filter, params?.sortBy, params?.orderBy]);
};


